import React, { useRef } from "react";
import { DefaultButton, Spinner, SpinnerSize } from "@fluentui/react";
import CompanySectionCard from "@/screen/Companies/components/CompanySectionCard";
import { CompanyOnboardingStrings } from "@/screen/Companies/companyOnboarding.strings";
import type { CompanyDocumentCategory, CompanyDocumentDto } from "@/services/CompanyOnboardingService";
import { DocumentBlock, DocumentTable, DropZone, LinkButton } from "@/screen/Companies/Styles/CompanyDetailsStyles";

interface CompanyDocumentsSectionProps
{
    id?: string;
    number: number;
    documents: CompanyDocumentDto[];
    uploadingCategory: CompanyDocumentCategory | null;
    disabled: boolean;
    onUpload: (category: CompanyDocumentCategory, files: File[]) => void;
    onDelete: (documentId: number) => void;
}

interface DocumentUploadBlockProps
{
    category: CompanyDocumentCategory;
    title: string;
    hint: string;
    // Formatos admitidos y texto de la zona de arrastre (por defecto solo PDF, lo único que admite Omnia)
    accept?: string;
    dropHint?: string;
    // Con categoría, la tabla tiene la columna Categoría (KYC)
    categoryLabel?: string;
    multiple: boolean;
    documents: CompanyDocumentDto[];
    uploading: boolean;
    disabled: boolean;
    onUpload: (category: CompanyDocumentCategory, files: File[]) => void;
    onDelete: (documentId: number) => void;
}

export const DocumentUploadBlock: React.FC<DocumentUploadBlockProps> = ({ category, title, hint, accept = ".pdf", dropHint = CompanyOnboardingStrings.companyDocumentDropHint, categoryLabel, multiple, documents, uploading, disabled, onUpload, onDelete }) =>
{
    const inputRef = useRef<HTMLInputElement>(null);

    const handleFiles = (files: FileList | null): void =>
    {
        if(files && files.length > 0)
        {
            onUpload(category, Array.from(files));
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>): void =>
    {
        e.preventDefault();
        if(!disabled && !uploading)
        {
            handleFiles(e.dataTransfer.files);
        }
    };

    return(
        <DocumentBlock>
            <b>{title} *</b>
            <small>{hint}</small>
            <DropZone onDragOver={(e) => e.preventDefault()} onDrop={handleDrop}>
                <span>{dropHint}</span>
                <input ref={inputRef} type="file" accept={accept} multiple={multiple}
                    onChange={(e) =>
                    {
                        handleFiles(e.target.files);
                        // Permite volver a elegir el mismo fichero
                        e.target.value = "";
                    }}
                />
                <DefaultButton className="button tiny-secondary-button" onClick={() => inputRef.current?.click()} disabled={disabled || uploading}>
                    {uploading && <Spinner size={SpinnerSize.small} style={{marginRight: "8px"}} />}
                    {uploading ? CompanyOnboardingStrings.uploadingDocument : multiple ? CompanyOnboardingStrings.selectFiles : CompanyOnboardingStrings.selectFile}
                </DefaultButton>
            </DropZone>
            <DocumentTable>
                <thead>
                    <tr>
                        <th>Nombre</th>
                        {categoryLabel && <th>Categoría</th>}
                        {categoryLabel ? <th>Acciones</th> : <th />}
                    </tr>
                </thead>
                <tbody>
                    {documents.length === 0 ?
                    (
                        <tr><td className="empty" colSpan={categoryLabel ? 3 : 2}>{CompanyOnboardingStrings.noDocuments}</td></tr>
                    ) :
                    (
                        documents.map((document) =>
                        (
                            <tr key={document.id}>
                                <td>{document.fileName}</td>
                                {categoryLabel && <td>{categoryLabel}</td>}
                                <td className="actions">
                                    <LinkButton type="button" onClick={() => onDelete(document.id)} disabled={disabled || uploading}>
                                        {CompanyOnboardingStrings.removeDocument}
                                    </LinkButton>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </DocumentTable>
        </DocumentBlock>
    );
};

// Sección 5: documentos de constitución (varios) y documento CIF (uno, sustituye al anterior)
const CompanyDocumentsSection: React.FC<CompanyDocumentsSectionProps> = ({ id, number, documents, uploadingCategory, disabled, onUpload, onDelete }) =>
{
    return(
        <CompanySectionCard id={id} number={number} title={CompanyOnboardingStrings.documentsTitle} description={CompanyOnboardingStrings.documentsDescription}>
            <DocumentUploadBlock category="Incorporation" title={CompanyOnboardingStrings.incorporationDocuments} hint={CompanyOnboardingStrings.incorporationDocumentsHint}
                multiple={true} documents={documents.filter((document) => document.category === "Incorporation")}
                uploading={uploadingCategory === "Incorporation"} disabled={disabled || uploadingCategory !== null} onUpload={onUpload} onDelete={onDelete}
            />
            <DocumentUploadBlock category="TaxIdCard" title={CompanyOnboardingStrings.taxIdDocument} hint={CompanyOnboardingStrings.taxIdDocumentHint}
                multiple={false} documents={documents.filter((document) => document.category === "TaxIdCard")}
                uploading={uploadingCategory === "TaxIdCard"} disabled={disabled || uploadingCategory !== null} onUpload={onUpload} onDelete={onDelete}
            />
        </CompanySectionCard>
    );
};

export default CompanyDocumentsSection;
